import { getMainProfileImage } from './databaseService';
import { getPlaceholderImage } from './placeholderService';
import { EscortProfile } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { isValidUuid, formatStorageUrl } from './imageUtils';

/**
 * Récupère l'URL de l'image principale d'un profil
 * Fonction utilitaire simplifiée pour les composants React
 * Retourne un placeholder si aucune image n'est disponible
 * @param profileIdOrPath Identifiant du profil ou chemin d'image
 * @param profile Objet profil optionnel (pour générer un placeholder spécifique)
 * @returns URL de l'image de profil ou d'un placeholder
 */
export const getProfileImageUrl = async (
  profileIdOrPath: string,
  profile?: EscortProfile | null
): Promise<string> => {
  try {
    // Check if the input is neither a path nor a valid UUID
    if (!isValidUuid(profileIdOrPath) && !profileIdOrPath.includes('/')) {
      console.log(`Non-UUID ID detected for profile ${profileIdOrPath}, using fallback image`);
      return getPlaceholderImage(profile);
    }
    
    // Check if the input is a path rather than an ID
    if (profileIdOrPath.includes('/')) {
      // It's a storage path, get the public URL
      return formatStorageUrl(profileIdOrPath);
    }
    
    console.log(`Fetching main image for profile ${profileIdOrPath}`);
    
    // Attempt to get the main profile image by ID
    const imagePath = await getMainProfileImage(profileIdOrPath);
    
    // If an image was found, return its formatted URL
    if (imagePath) {
      console.log(`Main image for profile ${profileIdOrPath}: ${imagePath}`);
      return formatStorageUrl(imagePath);
    }
    
    // Otherwise, return a placeholder
    return getPlaceholderImage(profile);
  } catch (error) {
    console.error(`Error getting profile image for ${profileIdOrPath}:`, error);
    // In case of error, use a placeholder
    return getPlaceholderImage(profile);
  }
};

/**
 * Version synchrone de getProfileImageUrl qui retourne uniquement le placeholder
 * Utile pour les états initiaux de chargement dans les composants React
 */
export const getInitialProfileImageUrl = (profile?: EscortProfile | null): string => {
  return getPlaceholderImage(profile);
};
